import { Button, message, Modal, Popover, Segmented, Tooltip } from "antd";
import InlineEditTitle from "components/inline-edit-title";
import { CiqIconBox, CopyIcon } from "components/svg-icons";
import { useCallback, useMemo, useEffect, useState } from "react";
import "./submittal-detail-header.scss";
import ErrorBoundary from "components/error-boundary";
import { useHistory, useLocation, useParams } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import FeatureDuplicate from "components/feature-duplicate";
import { FeatureTypes } from "entity-app/constants";
import MicrovizDateBlock from "components/microviz/microviz-dateblock";
import {
  getUserFromParticipantType,
  transformDataFromWFInstanceMilestones
} from "components/microviz/microviz-data-transformations";
import SubmittalMicroVisualiser from "components/submittal-details/submittal-micro-viz";
import { DateUtils } from "utils/dateutils";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_REDISTRIBUTE_SUBMITTAL } from "services/graphQL/mutations";
import { CloseAndDistributeSubmittal } from "components/close-and-distribute";
import ArrowUpBox from "components/svg-icons/arrow-right-up-box-line";
import {
  EWorkflowStatusDataBlock,
  IntegrationLinkTooltipMsg
} from "../../../../constants";
import FeatureSubmittalRevision from "../components/submittal-revision";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

function FeatureHeader(props: {
  featureInstanceData?: any;
  title: string;
  subTitle?: string;
  actions?: any;
  headerTitle?: string;
  number?: string;
  disabled: boolean;
  feature: "SUBMITTALS";
  goBackToList: () => void;
  getTabIndex: (tab: string) => void;
  submittal: any;
  projectIntegrationConfig: any;
  isIntegrationMode: boolean;
  setRevisionMode: any;
  submittalHeaderMap: any;
  tokenContents: any;
  setSubmittalViewState: any;
  gqlClientForProject: any;
  isRevisionMode?: boolean;
  updateFeatureData: {
    localFeatureInstanceData: any;
    setLocalFeatureInstanceData: Function;
  };
  readOnlyMode?: boolean;
}) {
  const {
    featureInstanceData,
    actions,
    title,
    subTitle,
    headerTitle,
    number,
    disabled,
    feature,
    goBackToList,
    getTabIndex,
    submittal,
    projectIntegrationConfig,
    isIntegrationMode,
    setRevisionMode,
    submittalHeaderMap,
    tokenContents,
    setSubmittalViewState,
    gqlClientForProject,
    updateFeatureData: {
      localFeatureInstanceData,
      setLocalFeatureInstanceData
    },
    readOnlyMode,
    isRevisionMode = false
  } = props;
  const { projectId, submittalId } = useParams() as any;
  const location = useLocation();
  const history = useHistory();
  const tab = new URLSearchParams(location.search).get("tab");
  const [tabIndex, setTabIndex] = useState<string>(tab || "1");
  const [submittalRedistributeUsers, setSubmittalRedistributeUsers] = useState(
    []
  );
  const [submittalRedistributing, setSubmittalRedistributed] = useState(false);
  const [openDistributeModel, setOpenDistributeSubmittalModel] =
    useState(false);

  const [visible, setVisible] = useState(false);
  const [headerInfo, setHeaderInfo] = useState<{
    featureHeaderNumber?: string;
    featureHeaderTitle?: string;
  }>();

  const { cannotCreateSubmittal, submittalParticipants } =
    useSubmmittialDetails({
      featureInstanceData: localFeatureInstanceData,
      isRevisionMode
    });

  const updateHeaderInfo = async (headersInfo: any) => {
    setHeaderInfo({
      featureHeaderNumber: headersInfo?.number,
      featureHeaderTitle: headersInfo?.title
    });
    await actions.updateFeatureInstanceFields({
      title: headersInfo?.title,
      number: headersInfo?.number
    });
  };

  const [redistributeSubmittal] = useCIQMutation(
    MUTATION_REDISTRIBUTE_SUBMITTAL,
    {
      client: gqlClientForProject
    }
  );

  useEffect(() => {
    setHeaderInfo({
      featureHeaderNumber: number,
      featureHeaderTitle: headerTitle
    });
  }, [headerTitle, number]);

  const segmentOptions = useMemo(() => {
    const options = [{ label: "Details", value: "1" }];
    if (!isIntegrationMode) options.push({ label: "Attachments", value: "2" });

    return options;
  }, [isIntegrationMode]);

  const redistributeClosedSubmittal = async () => {
    setSubmittalRedistributed(true);
    const responseData: any = await redistributeSubmittal({
      variables: {
        submittalId: featureInstanceData?.id,
        toAddresses: submittalRedistributeUsers
      }
    });
    setSubmittalRedistributed(false);
    if (responseData?.data?.redistribute_submittal?.message) {
      message.success("Submittal redistributed successfully.");
    } else {
      message.error("Failed to redistribute submittal.");
    }
  };

  const dataForMicroviz = useCallback(() => {
    const designReviewerUser = submittalParticipants?.drReviewer;
    const gcReviewerUser = submittalParticipants?.gcReviewer;

    return {
      submittal_history: submittal?.submittal_status_history_details,
      design_reviewer_id: designReviewerUser?.user?.id,
      gc_reviewer_id: gcReviewerUser?.user?.id,
      vendorInfo: {
        designReviewerCompanyName: designReviewerUser?.user?.project_users
          ?.length
          ? designReviewerUser?.user?.project_users[0]?.subscription_vendor
              ?.name
          : "",
        responsibleContractorCompanyName:
          submittal?.responsible_contractor_vendor?.name
      }
    };
  }, [
    submittal?.responsible_contractor_vendor?.name,
    submittal?.submittal_status_history_details,
    submittalParticipants?.drReviewer,
    submittalParticipants?.gcReviewer
  ]);

  const distributedDate =
    localFeatureInstanceData?.feature_workflow_instance?.length &&
    localFeatureInstanceData?.feature_workflow_instance[4]
      ? localFeatureInstanceData?.feature_workflow_instance[4]?.actual_date
      : null;

  return (
    <div
      className="flex flex-col w-full bg-white px-6 py-4 space-y-2"
      data-testid="submittal-header-root"
    >
      <div className="flex justify-between items-center">
        <div className="flex space-x-2 items-center">
          <Button
            icon={<ArrowLeftOutlined />}
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToList}
            data-testid="submittal-header-back-button"
          />
          <CiqIconBox feature={feature} size={20} padding={8} />
          <div className="flex-col pl-1">
            <div className="flex items-center space-x-2">
              <div
                className="text-xl font-semibold"
                data-testid="submittal-header-title"
              >
                {title}
              </div>
            </div>
            {subTitle && (
              <div
                className="text-xs font-normal"
                data-testid="submittal-header-subtitle"
              >
                {subTitle}
              </div>
            )}
          </div>
          {!readOnlyMode && segmentOptions.length && (
            <Segmented
              shape="round"
              size="large"
              value={tabIndex}
              options={segmentOptions}
              onChange={(value) => {
                setTabIndex(value as string);
                getTabIndex(value as string);
                history.push(
                  `/project/${projectId}/submittals/${submittalId}?tab=${value}`
                );
              }}
              data-testid="submittal-header-tab-segmented"
            />
          )}
        </div>
        {!isIntegrationMode &&
          submittal?.workflow_status ===
            EWorkflowStatusDataBlock.WF_COMPLETED && (
            <div className="flex space-x-2 items-center">
              <div
                className="font-semibold"
                data-testid="submittal-header-distributed-label"
              >
                Submittal Distributed: {DateUtils.format(distributedDate)}
              </div>
              {!readOnlyMode && (
                <div>
                  <Button
                    onClick={() => {
                      setOpenDistributeSubmittalModel(true);
                    }}
                    loading={submittalRedistributing}
                    disabled={submittalRedistributing}
                    data-testid="index-button-redistribute"
                  >
                    Redistribute
                  </Button>
                  <Modal
                    title="Redistribute Submittal"
                    width={500}
                    open={openDistributeModel}
                    okButtonProps={{
                      disabled: submittalRedistributeUsers?.length === 0
                    }}
                    onOk={() => {
                      if (submittalRedistributeUsers?.length > 0) {
                        setOpenDistributeSubmittalModel(false);
                        redistributeClosedSubmittal();
                      }
                    }}
                    onCancel={() => {
                      setOpenDistributeSubmittalModel(false);
                    }}
                    okText="Redistribute"
                    destroyOnClose
                    data-testid="index-modal-redistribute"
                  >
                    <CloseAndDistributeSubmittal
                      submittalData={featureInstanceData}
                      submittalWorkflowResponses={[]}
                      commentVal=""
                      redistributeSubmittalToUsers={(selectedusers: any) => {
                        setSubmittalRedistributeUsers(selectedusers);
                      }}
                      submittalViewState={{
                        status: submittal?.workflow_status,
                        userType: tokenContents?.userType
                      }}
                      data-testid="submittal-header-redistribute-component"
                    />
                  </Modal>
                </div>
              )}
            </div>
          )}

        <div className="flex space-x-2">
          {!isIntegrationMode && !readOnlyMode && (
            <Popover
              open={!cannotCreateSubmittal && visible}
              placement="bottomRight"
              content={
                <ErrorBoundary>
                  <FeatureDuplicate
                    featureInstanceData={featureInstanceData}
                    actions={actions}
                    currentFeatureType={FeatureTypes.SUBMITTALS}
                    linkedSourcefeatureType={FeatureTypes.MATERIALS}
                    setVisible={setVisible}
                    visible={visible}
                    data-testid="submittal-header-duplicate-component"
                  />
                </ErrorBoundary>
              }
              trigger="click"
              onOpenChange={() => {
                if (!cannotCreateSubmittal) setVisible(!visible);
              }}
              destroyTooltipOnHide
              data-testid="submittal-header-duplicate-popover"
            >
              <Tooltip data-testid="submittal-header-duplicate-tooltip">
                <Button
                  onClick={() => setVisible(true)}
                  disabled={cannotCreateSubmittal}
                  data-testid="index-button-duplicate"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className=" text-[12px]"
                      data-testid="submittal-header-duplicate-text"
                    >
                      Duplicate
                    </div>
                    <CopyIcon />
                  </div>
                </Button>
              </Tooltip>
            </Popover>
          )}
          <FeatureSubmittalRevision
            featureInstanceData={featureInstanceData}
            localFeatureInstanceData={localFeatureInstanceData}
            setLocalFeatureInstanceData={setLocalFeatureInstanceData}
            submittal={submittal}
            projectIntegrationConfig={projectIntegrationConfig}
            isIntegrationMode={isIntegrationMode}
            setRevisionMode={setRevisionMode}
            submittalHeaderMap={submittalHeaderMap}
            tokenContents={tokenContents}
            setSubmittalViewState={setSubmittalViewState}
            data-testid="submittal-header-revision-component"
          />
          {localFeatureInstanceData?.redirect_url && (
            <Tooltip
              placement="right"
              title={
                projectIntegrationConfig?.length
                  ? IntegrationLinkTooltipMsg[
                      projectIntegrationConfig[0]?.integration
                        ?.system as keyof typeof IntegrationLinkTooltipMsg
                    ]
                  : ""
              }
              data-testid="submittal-header-integration-tooltip"
            >
              <Button
                className="icon-link-arrow button-spacing"
                icon={<ArrowUpBox />}
                color="default"
                type="link"
                onClick={() => {
                  window.open(localFeatureInstanceData?.redirect_url, "_blank");
                }}
                data-testid="submittal-header-integration-link-button"
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex py-2 md:px-16 lg:px-24 sm:px-4 items-center justify-center">
        <div className="flex w-full p-4 bg-ciq-gray-400 bg-opacity-60 items-center justify-between">
          <div
            className="flex items-center space-x-10"
            data-testid="submittal-header-inline-edit"
          >
            <ErrorBoundary>
              <InlineEditTitle
                isPermissionToEdit={disabled}
                headerInfo={headerInfo}
                updateHeaderInfo={updateHeaderInfo}
                feature={feature}
                data-testid="submittal-header-inline-edit-title"
              />
            </ErrorBoundary>
            <div className="flex ml-1">
              {submittalHeaderMap?.revision}:{" "}
              {localFeatureInstanceData?.revision ?? "0"}
            </div>
          </div>
          <div className="flex items-center space-x-10">
            {featureInstanceData && isIntegrationMode && (
              <div className="w-[260px] text-right">
                <ErrorBoundary>
                  <MicrovizDateBlock
                    submittalData={{
                      ...featureInstanceData,
                      revision: 0,
                      revision_date_blocks: []
                    }}
                    submittalRevisions={[]}
                    selectedRevision={0}
                    dataTransformFunction={
                      transformDataFromWFInstanceMilestones
                    }
                    userGetterFunction={getUserFromParticipantType}
                    data-testid="submittal-header-microviz-dateblock"
                  />
                </ErrorBoundary>
              </div>
            )}

            {featureInstanceData && !isIntegrationMode && (
              <div className="w-[260px] text-right">
                <ErrorBoundary>
                  <SubmittalMicroVisualiser
                    {...dataForMicroviz()}
                    titleBar
                    data-testid="submittal-header-microviz-visualiser"
                  />
                </ErrorBoundary>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureHeader;
